<template>
  <UiContainer class="order-view">
    <div class="order-view__head">
      <UiButton
        variant="secondary"
        class="order-view__back"
        @click="$router.back()"
        aria-label="Назад"
      >
        ←
      </UiButton>
      <h1 class="order-view__title">
        {{ order?.status === 'draft' ? 'Черновик заказа' : 'Заказ' }} №
        {{ order?.order_number || order?.number || route.params['id'] }}
      </h1>
    </div>

    <div v-if="loading" class="order-view__state">
      <UiProgress variant="spinner" />
      <p>Загрузка заказа...</p>
    </div>

    <div v-else-if="error" class="order-view__state order-view__state--error">
      <span class="order-view__state-icon">!</span>
      <h3 class="order-view__state-title">Заказ не найден</h3>
      <p class="order-view__state-text">{{ error }}</p>
      <UiButton to="/orders">Вернуться к заказам</UiButton>
    </div>

    <template v-else-if="order">
      <div class="order-view__grid">
        <div class="order-view__main">
          <UiCard class="order-view__card">
            <div class="order-view__card-head">
              <div>
                <UiBadge
                  :variant="getOrderStatusColor(order.status) === 'success' ? 'accent' : 'neutral'"
                >
                  {{ getOrderStatusText(order.status) }}
                </UiBadge>
              </div>
              <div class="order-view__actions">
                <UiButton v-if="order.status === 'draft'" @click="resumeDraft">
                  Продолжить оформление
                </UiButton>
                <UiButton v-if="canRepeatOrder" variant="secondary" @click="repeatOrder">
                  Повторить заказ
                </UiButton>
                <UiButton v-if="canCancelOrder" variant="secondary" @click="showCancelDialog">
                  {{ order.status === 'draft' ? 'Удалить черновик' : 'Отменить заказ' }}
                </UiButton>
              </div>
            </div>

            <p
              v-if="order.status === 'cancelled' && order.cancellation_reason"
              class="order-view__cancel-reason"
            >
              Заказ отменён. Причина: <strong>{{ order.cancellation_reason }}</strong>
            </p>

            <div class="order-view__timeline">
              <h3 class="order-view__timeline-title">Статус заказа</h3>
              <ul class="order-view__timeline-list">
                <li
                  v-for="(step, index) in orderTimeline"
                  :key="index"
                  class="order-view__timeline-item"
                  :class="{ 'order-view__timeline-item--done': step.completed }"
                >
                  <span class="order-view__timeline-dot" />
                  <div class="order-view__timeline-content">
                    <span class="order-view__timeline-date">{{ step.date || '' }}</span>
                    <span class="order-view__timeline-label">{{ step.title }}</span>
                    <span v-if="step.description" class="order-view__timeline-desc">{{
                      step.description
                    }}</span>
                  </div>
                </li>
              </ul>
            </div>
          </UiCard>

          <UiCard class="order-view__card">
            <h3 class="order-view__card-title">Товары в заказе</h3>
            <div v-for="item in order?.items || []" :key="item.id" class="order-view__item">
              <component
                :is="item.product?.id ? 'router-link' : 'span'"
                :to="item.product?.id ? `/product/${item.product.id}` : undefined"
                class="order-view__item-img-link"
              >
                <img
                  :src="getProductImage(item)"
                  alt=""
                  class="order-view__item-img"
                  width="80"
                  height="80"
                  @error="handleProductImageError"
                />
              </component>
              <div class="order-view__item-info">
                <component
                  :is="item.product?.id ? 'router-link' : 'span'"
                  :to="item.product?.id ? `/product/${item.product.id}` : undefined"
                  class="order-view__item-name"
                >
                  {{ item.product?.name || 'Товар' }}
                </component>
                <p class="order-view__item-sku">{{ item.product?.sku || 'Артикул' }}</p>
                <p class="order-view__item-qty">
                  {{ formatPriceWithCents(item.unitPrice) }} × {{ item.quantity || 0 }} шт.
                </p>
              </div>
              <div class="order-view__item-total">{{ formatPriceWithCents(item.totalPrice) }}</div>
            </div>
            <div class="order-view__total">
              <span>Итого:</span>
              <span>{{
                formatPriceWithCents(order?.final_amount || order?.total_amount || order?.totalAmount)
              }}</span>
            </div>
          </UiCard>

          <UiCard v-if="order.comment" class="order-view__card">
            <h3 class="order-view__card-title">Комментарий к заказу</h3>
            <p class="order-view__comment">{{ order.comment }}</p>
          </UiCard>
        </div>

        <aside class="order-view__aside">
          <UiCard class="order-view__card order-view__card--sticky">
            <h3 class="order-view__card-title">Информация о заказе</h3>
            <dl class="order-view__meta">
              <div class="order-view__meta-row">
                <dt>Номер заказа:</dt>
                <dd>{{ order?.order_number || order?.number || 'Не указан' }}</dd>
              </div>
              <div class="order-view__meta-row">
                <dt>Дата создания:</dt>
                <dd>{{ formatDate(order?.created_at || order?.createdAt) }}</dd>
              </div>
              <div
                v-if="
                  (order?.updated_at || order?.updatedAt) &&
                  (order?.updated_at || order?.updatedAt) !==
                    (order?.created_at || order?.createdAt)
                "
                class="order-view__meta-row"
              >
                <dt>Последнее обновление:</dt>
                <dd>{{ formatDate(order?.updated_at || order?.updatedAt) }}</dd>
              </div>
              <div class="order-view__meta-row">
                <dt>Количество товаров:</dt>
                <dd>
                  {{ order?.items_count || order?.itemsCount || order?.items?.length || 0 }}
                  {{
                    getItemsText(
                      order?.items_count || order?.itemsCount || order?.items?.length || 0,
                    )
                  }}
                </dd>
              </div>
              <div class="order-view__meta-row">
                <dt>Сумма заказа:</dt>
                <dd class="order-view__meta-sum">
                  {{
                    formatPriceWithCents(order?.final_amount || order?.total_amount || order?.totalAmount)
                  }}
                </dd>
              </div>
            </dl>
          </UiCard>

          <UiCard class="order-view__card">
            <h3 class="order-view__card-title">Нужна помощь?</h3>
            <p class="order-view__help-text">
              Если у вас есть вопросы по заказу, свяжитесь с нами:
            </p>
            <div class="order-view__help-links">
              <a href="tel:+79518520624" class="order-view__link">+7 (951) 852-06-24</a>
              <a href="mailto:support@labactive.ru" class="order-view__link">support@labactive.ru</a>
              <UiButton v-if="isChatEnabled" variant="secondary" @click="openChat">
                Онлайн чат
              </UiButton>
            </div>
          </UiCard>
        </aside>
      </div>
    </template>

    <UiModal v-model="cancelDialog" title="Отмена заказа" :max-width="500">
      <p>
        Вы уверены, что хотите
        {{ order?.status === 'draft' ? 'удалить черновик' : 'отменить заказ' }} №
        {{ order?.order_number || order?.number }}?
      </p>
      <p class="order-view__modal-note">Это действие нельзя будет отменить.</p>
      <template #footer>
        <UiButton variant="secondary" @click="cancelDialog = false">Отмена</UiButton>
        <UiButton :loading="cancelLoading" @click="confirmCancelOrder">
          {{ order?.status === 'draft' ? 'Удалить черновик' : 'Отменить заказ' }}
        </UiButton>
      </template>
    </UiModal>

    <UiModal v-model="supportChatDialog" :title="supportChatTitle" :max-width="640">
      <div v-if="supportChatLoading" class="order-view__chat-state">
        <UiProgress variant="spinner" />
        <p>Загрузка чата...</p>
      </div>
      <div
        v-else-if="supportChatError"
        class="order-view__chat-state order-view__chat-state--error"
      >
        <p>{{ supportChatError }}</p>
      </div>
      <div v-else class="order-view__chat">
        <div class="order-view__chat-messages">
          <div v-if="!supportMessages.length" class="order-view__chat-empty">
            Сообщений пока нет
          </div>
          <div
            v-for="message in supportMessages"
            :key="message.id"
            class="order-view__chat-message"
            :class="{
              'order-view__chat-message--own':
                message.author.id === chatSupport.currentUserId.value,
            }"
          >
            <div class="order-view__chat-author">{{ message.author.name || 'Пользователь' }}</div>
            <div class="order-view__chat-content">{{ message.content }}</div>
            <div class="order-view__chat-time">{{ formatDate(message.created_at) }}</div>
          </div>
        </div>

        <form class="order-view__chat-form" @submit.prevent="sendSupportMessage">
          <UiInput v-model="supportMessageText" placeholder="Напишите сообщение" />
          <UiButton type="submit" :disabled="!canSendSupportMessage">Отправить</UiButton>
        </form>
      </div>
    </UiModal>
  </UiContainer>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useOrderDetail, type OrderItem } from '@/modules/order/public'
import apiClient from '@/api/client'
import { useChatSupport } from '@/modules/post-mvp/chat/public'
import { isFeatureEnabled, useAppNotifications } from '@/modules/common/public'
import { formatPriceWithCents } from '@/utils/price'
import UiBadge from '@/shared/ui/UiBadge.vue'
import UiButton from '@/shared/ui/UiButton.vue'
import UiCard from '@/shared/ui/UiCard.vue'
import UiContainer from '@/shared/ui/UiContainer.vue'
import UiInput from '@/shared/ui/UiInput.vue'
import UiModal from '@/shared/ui/UiModal.vue'
import UiProgress from '@/shared/ui/UiProgress.vue'

const route = useRoute()
const router = useRouter()
const orderStore = useOrderDetail()
const chatSupport = useChatSupport()
const isChatEnabled = computed(() => isFeatureEnabled('chat_enabled'))
const notificationStore = useAppNotifications()

const loading = ref(false)
const error = ref<string | null>(null)
const cancelDialog = ref(false)
const cancelLoading = ref(false)
const supportChatDialog = ref(false)
const supportChatLoading = ref(false)
const supportChatError = ref<string | null>(null)
const selectedSupportChatId = ref<number | null>(null)
const supportMessageText = ref('')

const order = computed(() => orderStore.currentOrder.value)
const canCancelOrder = computed(() =>
  order.value ? orderStore.canCancelOrder(order.value) : false,
)
const canRepeatOrder = computed(() =>
  order.value ? orderStore.canRepeatOrder(order.value) : false,
)
const supportChat = computed(() =>
  selectedSupportChatId.value !== null
    ? chatSupport.getChatById(selectedSupportChatId.value)
    : null,
)
const supportMessages = computed(() =>
  selectedSupportChatId.value !== null
    ? chatSupport.getMessagesByChatId(selectedSupportChatId.value)
    : [],
)
const supportChatTitle = computed(() => supportChat.value?.name || 'Чат поддержки')
const canSendSupportMessage = computed(
  () => selectedSupportChatId.value !== null && supportMessageText.value.trim().length > 0,
)

const orderTimeline = computed(() => {
  if (!order.value) return []

  const created_date = formatDate(order.value.created_at || order.value.createdAt)
  if (order.value.status === 'draft') {
    return [
      {
        title: 'Черновик сохранён',
        description: 'Заказ ещё не отправлен поставщику',
        completed: true,
        date: created_date,
      },
    ]
  }

  if (order.value.deleted_from_draft) {
    return [
      {
        title: 'Черновик удалён',
        description: 'Заказ не был отправлен поставщику',
        completed: true,
        date: formatDate(order.value.updated_at || order.value.updatedAt || created_date),
      },
    ]
  }

  const timeline = [
    {
      title: 'Заказ создан',
      description:
        order.value.status === 'pending'
          ? 'Ожидает подтверждения поставщика'
          : 'Заказ оформлен и готов к обработке',
      completed: true,
      date: created_date,
    },
    {
      title: 'Заказ подтвержден',
      description: 'Передан в обработку',
      completed: ['confirmed', 'processing', 'shipped', 'delivered'].includes(order.value.status),
    },
    {
      title: 'В обработке',
      description: 'Комплектация заказа',
      completed: ['processing', 'shipped', 'delivered'].includes(order.value.status),
    },
    {
      title: 'Отправлен',
      description: 'Заказ передан в доставку',
      completed: ['shipped', 'delivered'].includes(order.value.status),
    },
    {
      title: 'Доставлен',
      description: 'Заказ получен клиентом',
      completed: order.value.status === 'delivered',
    },
  ]
  if (order.value.status === 'cancelled') {
    const cancelledStep: {
      title: string
      description: string
      completed: boolean
      date?: string
    } = {
      title: 'Заказ отменен',
      description: order.value.cancellation_reason
        ? `Причина: ${order.value.cancellation_reason}`
        : 'Заказ был отменен',
      completed: true,
    }
    if (order.value.updated_at || order.value.updatedAt) {
      cancelledStep.date = formatDate(order.value.updated_at || order.value.updatedAt)
    }
    timeline.push(cancelledStep)
  }
  return timeline
})

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return 'Дата не указана'
  try {
    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) return 'Неверная дата'
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return 'Ошибка даты'
  }
}

function getItemsText(count: number): string {
  const lastDigit = count % 10
  const lastTwoDigits = count % 100
  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) return 'товаров'
  switch (lastDigit) {
    case 1:
      return 'товар'
    case 2:
    case 3:
    case 4:
      return 'товара'
    default:
      return 'товаров'
  }
}

function getOrderStatusText(status: string): string {
  return orderStore.getOrderStatusText(status)
}

function getOrderStatusColor(status: string): string {
  return orderStore.getOrderStatusColor(status)
}

function getProductImage(item: OrderItem): string {
  return item.product?.imageUrl || '/placeholder-product.svg'
}

function handleProductImageError(event: Event) {
  const target = event.target
  if (!(target instanceof HTMLImageElement)) return
  if (target.src.endsWith('/placeholder-product.svg')) return
  target.src = '/placeholder-product.svg'
}

async function loadOrder() {
  const orderId = String(route.params['id'] ?? '')
  loading.value = true
  error.value = null
  try {
    const result = await orderStore.fetchOrder(orderId)
    if (!result.success) error.value = result.error || 'Заказ не найден'
  } catch (err) {
    console.error('Ошибка загрузки заказа:', err)
    error.value = 'Произошла ошибка при загрузке заказа'
  } finally {
    loading.value = false
  }
}

function showCancelDialog() {
  cancelDialog.value = true
}

function resumeDraft() {
  if (!order.value || order.value.status !== 'draft') return

  router.push({
    path: '/checkout',
    query: { draftId: String(order.value.id) },
  })
}

async function confirmCancelOrder() {
  if (!order.value) return
  const wasDraft = order.value.status === 'draft'
  cancelLoading.value = true
  try {
    const result = await orderStore.cancelOrder(String(order.value.id))
    if (result.success) {
      cancelDialog.value = false
      if (wasDraft) {
        notificationStore.showSuccess('Черновик удалён')
        router.push('/orders')
        return
      }
      notificationStore.showSuccess('Заказ успешно отменен')
      await loadOrder()
    } else {
      notificationStore.showError(
        result.message || (wasDraft ? 'Не удалось удалить черновик' : 'Не удалось отменить заказ'),
      )
    }
  } catch (err) {
    console.error('Ошибка отмены заказа:', err)
    notificationStore.showError(
      wasDraft ? 'Произошла ошибка при удалении черновика' : 'Произошла ошибка при отмене заказа',
    )
  } finally {
    cancelLoading.value = false
  }
}

async function repeatOrder() {
  if (!order.value) return

  try {
    const res = await apiClient.post(`/api/orders/${order.value.id}/repeat`)
    if (res.data.success) {
      notificationStore.showSuccess('Черновик заказа создан')
      router.push('/orders')
    } else {
      notificationStore.showError(res.data.message || 'Ошибка при повторе заказа')
    }
  } catch {
    notificationStore.showError('Ошибка при повторе заказа')
  }
}

async function openChat() {
  supportChatDialog.value = true
  supportChatLoading.value = true
  supportChatError.value = null

  try {
    const orderId = Number(order.value?.id ?? route.params['id'] ?? 0)
    await chatSupport.loadChats({
      page: 1,
      limit: 20,
      sort: 'last_activity_at',
      direction: 'desc',
    })

    const preferredChat = chatSupport.chats.value.find(
      (chat) => chat.context_type === 'order_support' && chat.context_id === orderId,
    )

    if (!preferredChat) {
      const orderNumber =
        order.value?.order_number || order.value?.number || String(route.params['id'] ?? '')

      const createdChat = await chatSupport.createChat({
        name: orderNumber ? `Поддержка по заказу ${orderNumber}` : 'Чат поддержки',
        type: 'group',
        description: orderNumber
          ? `Обращение клиента по заказу ${orderNumber}`
          : 'Обращение клиента в поддержку',
        ...(Number.isFinite(orderId) && orderId > 0
          ? {
              contextType: 'order_support',
              contextId: orderId,
            }
          : {}),
      })

      selectedSupportChatId.value = createdChat.id
      await loadSupportMessages(createdChat.id)
      return
    }

    selectedSupportChatId.value = preferredChat.id
    await loadSupportMessages(preferredChat.id)
  } catch (err) {
    console.error('Ошибка загрузки чата:', err)
    supportChatError.value = 'Не удалось загрузить чат поддержки'
  } finally {
    supportChatLoading.value = false
  }
}

async function loadSupportMessages(chatId: number) {
  try {
    await chatSupport.loadMessages(chatId)
  } catch (err) {
    console.error('Ошибка загрузки истории сообщений:', err)
    chatSupport.clearMessages(chatId)
  }
}

async function sendSupportMessage() {
  if (!selectedSupportChatId.value) return
  const content = supportMessageText.value.trim()
  if (!content) return

  try {
    await chatSupport.sendMessage({
      chatId: selectedSupportChatId.value,
      content,
    })
    supportMessageText.value = ''
  } catch (err) {
    console.error('Ошибка отправки сообщения:', err)
    notificationStore.showError('Не удалось отправить сообщение')
  }
}

onMounted(() => loadOrder())
</script>

<style scoped>
.order-view {
  padding-top: 24px;
  padding-bottom: 48px;
}

.order-view__head {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}

.order-view__back {
  flex-shrink: 0;
  padding: 8px 12px;
}

.order-view__title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
}

.order-view__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 16px;
  gap: 16px;
  color: #586578;
}

.order-view__state--error .order-view__state-icon {
  color: #dc2626;
}

.order-view__state-icon {
  font-size: 48px;
  line-height: 1;
}

.order-view__state-title {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 600;
}

.order-view__state-text {
  margin: 0 0 24px;
  text-align: center;
}

.order-view__grid {
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 24px;
  align-items: start;
}

@media (max-width: 1200px) {
  .order-view__grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

.order-view__main {
  min-width: 0;
}

.order-view__card {
  padding: 20px;
  margin-bottom: 24px;
}

.order-view__card--sticky {
  position: sticky;
  top: 100px;
}

.order-view__card-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
}

.order-view__card-title {
  margin: 0 0 16px;
  font-size: 16px;
  font-weight: 600;
}

.order-view__card-head .order-view__card-title {
  margin: 0 8px 0 0;
}

.order-view__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.order-view__cancel-reason {
  margin: 12px 0 0;
  padding: 10px 14px;
  border: 1px solid #ef4444;
  border-radius: 8px;
  background: #fee2e2;
  color: #7f1d1d;
  font-size: 14px;
}

.order-view__timeline-title {
  margin: 0 0 12px;
  font-size: 14px;
  font-weight: 600;
}

.order-view__timeline-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.order-view__timeline-item {
  display: flex;
  gap: 12px;
  padding: 8px 0;
  border-left: 2px solid #e2e8f0;
  margin-left: 6px;
  padding-left: 16px;
  position: relative;
}

.order-view__timeline-item--done {
  border-left-color: var(--color-accent);
}

.order-view__timeline-dot {
  position: absolute;
  left: -7px;
  top: 14px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #e2e8f0;
}

.order-view__timeline-item--done .order-view__timeline-dot {
  background: var(--color-accent);
}

.order-view__timeline-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.order-view__timeline-date {
  font-size: 12px;
  color: #586578;
}

.order-view__timeline-label {
  font-weight: 500;
}

.order-view__timeline-item:not(.order-view__timeline-item--done) .order-view__timeline-label {
  color: #586578;
}

.order-view__timeline-desc {
  font-size: 13px;
  color: #586578;
}

.order-view__item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid #e2e8f0;
}

.order-view__item:last-of-type {
  border-bottom: none;
}

.order-view__item-img {
  flex-shrink: 0;
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 6px;
  background: #f1f5f9;
}

.order-view__item-info {
  flex: 1;
  min-width: 0;
}

.order-view__item-img-link {
  display: block;
  flex-shrink: 0;
}

.order-view__item-name {
  display: block;
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 600;
  color: inherit;
  text-decoration: none;
}

a.order-view__item-name:hover {
  text-decoration: underline;
}

.order-view__item-sku,
.order-view__item-qty {
  margin: 0;
  font-size: 13px;
  color: #586578;
}

.order-view__item-total {
  font-weight: 600;
}

.order-view__total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  margin-top: 16px;
  border-top: 1px solid #e2e8f0;
  font-weight: 600;
}

.order-view__comment {
  margin: 0;
  font-size: 14px;
  color: #475569;
}

.order-view__meta {
  margin: 0;
}

.order-view__meta-row {
  margin-bottom: 12px;
}

.order-view__meta-row:last-child {
  margin-bottom: 0;
}

.order-view__meta-row dt {
  font-size: 12px;
  color: #586578;
  margin-bottom: 2px;
}

.order-view__meta-row dd {
  margin: 0;
  font-weight: 500;
}

.order-view__meta-sum {
  font-size: 18px;
  color: #0f172a;
}

.order-view__help-text {
  margin: 0 0 16px;
  font-size: 14px;
  color: #586578;
}

.order-view__help-links {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.order-view__link {
  color: #0f172a;
  font-weight: 500;
  text-decoration: none;
}

.order-view__link:hover {
  text-decoration: underline;
}

.order-view__modal-note {
  margin: 8px 0 0;
  font-size: 14px;
  color: #586578;
}

.order-view__chat-state {
  display: flex;
  min-height: 240px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #586578;
}

.order-view__chat-state--error {
  color: #dc2626;
}

.order-view__chat {
  display: flex;
  min-height: 420px;
  flex-direction: column;
  gap: 16px;
}

.order-view__chat-messages {
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
  background: #f8fafc;
}

.order-view__chat-empty {
  margin: auto;
  color: #586578;
}

.order-view__chat-message {
  max-width: 85%;
  align-self: flex-start;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
  padding: 10px 12px;
}

.order-view__chat-message--own {
  align-self: flex-end;
  border-color: #bfdbfe;
  background: #eff6ff;
}

.order-view__chat-author {
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
}

.order-view__chat-content {
  white-space: pre-wrap;
  word-break: break-word;
  color: #0f172a;
}

.order-view__chat-time {
  margin-top: 6px;
  font-size: 12px;
  color: #586578;
}

.order-view__chat-form {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  align-items: center;
}
</style>
